/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { trailCreate, trailUpdate, trailDelete, TRAIL_MODULES } from '../../utils/activityTrail';
import {
    Card, Button, Table, Modal, Select, message,
    Tag, Row, Col, Checkbox, Progress, Divider,
    Tooltip, InputNumber, Spin, Badge
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, CalendarOutlined,
    ThunderboltOutlined, TeamOutlined, WarningFilled,
    CopyOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { usePeriods } from '../../hooks/usePeriods';
import FacilityService from '../../services/FacilityService';
import { AntdSelect, AntdDatepicker } from '../../utils/Antd';
import axios from '../../utils/axios';
import util from '../../utils/util';
import moment from 'moment';

const { TextArea } = require('antd').Input;
const { Option, OptGroup } = Select;

const STATUS_CFG = {
    scheduled:   { color:'#1890ff', label:'Scheduled',   bg:'#e6f4ff' },
    in_progress: { color:'#faad14', label:'In Progress', bg:'#fffbe6' },
    completed:   { color:'#52c41a', label:'Completed',   bg:'#f6ffed' },
    overdue:     { color:'#f5222d', label:'Overdue',     bg:'#fff2f0' },
    cancelled:   { color:'#bbb',    label:'Cancelled',   bg:'#f5f5f5' },
};

const FACILITY_LEVELS = ['Clinic without Maternity','Clinic with Maternity','Public Health Unit','Regional Hospital','National Referral Hospital'];

// Build a colour for a clause — fall back to its standard's accent
function clauseColor(clause, standards) {
    if (!clause) return '#1890ff';
    const std = standards.find(s => s.id === clause.standard_id);
    return std?.accent || '#1890ff';
}

// Duplicate check helper
function findDuplicates(existingSchedules, candidateRows) {
    const existingKeys = new Set(
        existingSchedules
            .filter(s => s.status !== 'cancelled')
            .map(s => `${s.facility_id}|${s.clause_id || ''}|${moment(s.scheduled_date).year()}`)
    );
    const dupes = [];
    for (const row of candidateRows) {
        const year = moment(row.scheduled_date).year();
        const key  = `${row.facility_id}|${row.clause_id || ''}|${year}`;
        if (existingKeys.has(key)) {
            dupes.push({
                facility_name: row.facility_name || row.facility_id,
                clause_code:   row.clause_code   || '(full audit)',
                year,
            });
        }
    }
    return dupes;
}

export default function QoCSchedules() {
    const [schedules,  setSchedules]  = useState([]);
    const [summary,    setSummary]    = useState({});
    const [facilities, setFacilities] = useState([]);
    const [clauses,    setClauses]    = useState([]);
    const [standards,  setStandards]  = useState([]);   // NEW — for grouping
    const [users,      setUsers]      = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [filters,    setFilters]    = useState({ status:'', facility_id:'' });
    const isAdmin = (util.getModules?.()?.qoc_admin === 1) ||
                    parseInt(localStorage.getItem('is_admin') || '0') === 1;

    const [singleOpen,    setSingleOpen]    = useState(false);
    const [editSched,     setEditSched]     = useState(null);
    const [singleForm,    setSingleForm]    = useState({});

    const [bulkOpen,      setBulkOpen]      = useState(false);
    const [bulkForm,      setBulkForm]      = useState({
        selection_mode: 'level',
        facility_level: null,
        region: null,
        custom_facility_ids: [],
        clause_ids: [],
        scheduled_date: null,
        window_start: null,
        window_end: null,
        lead_assessor_id: null,
        assigned_assessors: [],
        notes: '',
    });
    const [bulkFacilities, setBulkFacilities] = useState([]);
    const [bulkPreview,    setBulkPreview]    = useState([]);
    const [bulkSaving,     setBulkSaving]     = useState(false);
    const { periods: periodsList, currentPeriod: currentSchedPeriod } = usePeriods();

    useEffect(() => {
        if (currentSchedPeriod) {
            setBulkForm(f => ({
                ...f,
                scheduled_date: moment(currentSchedPeriod.period_start),
                window_end:     moment(currentSchedPeriod.period_end),
                window_start:   moment(currentSchedPeriod.period_start),
            }));
        }
    }, [currentSchedPeriod]);

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data||[]));
        // Load standards first, then clauses (so we can group)
        QoCService.getStandards().then(r => setStandards(r.data?.data||[]));
        QoCService.getClauses().then(r => setClauses(r.data?.data||[]));
        axios.get('qoc/assessors').then(r => {
            const raw = r.data?.data || [];
            setUsers(raw.map(u => ({
                ...u,
                label: u.name + (u.facility_name ? ` (${u.facility_name})` : ''),
            })));
        });
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            let res;
            if (isAdmin) {
                // Admins see all schedules and can manage them
                res = await QoCService.getAuditSchedules({
                    ...(filters.status      && { status: filters.status }),
                    ...(filters.facility_id && { facility_id: filters.facility_id }),
                });
                setSchedules(res.data?.data || []);
                setSummary(res.data?.summary || {});
            } else {
                // Assessors only see schedules assigned to them
                res = await QoCService.getSchedulesForAssessor();
                setSchedules(res.data?.data || []);
                setSummary({});
            }
        } catch { message.error('Failed to load schedules'); }
        finally { setLoading(false); }
    };

    // Group clauses by standard for dropdowns
    const clausesByStandard = standards.map(std => ({
        standard: std,
        clauses:  clauses.filter(c => c.standard_id === std.id),
    })).filter(g => g.clauses.length > 0);

    // Clauses with no standard assigned (fallback group)
    const orphanClauses = clauses.filter(c => !c.standard_id || !standards.find(s => s.id === c.standard_id));

    // Render grouped clause options for a Select
    const renderClauseOptGroups = () => [
        ...clausesByStandard.map(({ standard, clauses: cls }) => (
            <OptGroup key={standard.id} label={
                <span style={{ fontWeight: 700, color: standard.accent || '#555' }}>
                    {standard.name}
                </span>
            }>
                {cls.map(c => (
                    <Option key={c.id} value={c.id}>
                        <span style={{ fontWeight: 700, color: standard.accent || '#1890ff', marginRight: 6 }}>
                            {c.code}
                        </span>
                        <span style={{ fontSize: 12, color: '#555' }}>{c.title}</span>
                    </Option>
                ))}
            </OptGroup>
        )),
        ...(orphanClauses.length > 0 ? [
            <OptGroup key="other" label="Other">
                {orphanClauses.map(c => (
                    <Option key={c.id} value={c.id}>{c.code} — {c.title}</Option>
                ))}
            </OptGroup>
        ] : []),
    ];

    const openNew  = () => {
        setEditSched(null);
        const initForm = { status:'scheduled' };
        if (currentSchedPeriod) {
            initForm.window_start = moment(currentSchedPeriod.period_start);
            initForm.window_end   = moment(currentSchedPeriod.period_end);
        }
        setSingleForm(initForm);
        setSingleOpen(true);
    };
    const openEdit = (s) => {
        setEditSched(s);
        let assigned = [];
        try {
            assigned = s.assigned_assessors ? JSON.parse(s.assigned_assessors) : [];
            assigned = assigned.map(Number).filter(Boolean);
        } catch (e) { assigned = []; }
        setSingleForm({ ...s,
            scheduled_date:     s.scheduled_date ? moment(s.scheduled_date) : null,
            window_start:       s.window_start   ? moment(s.window_start)   : null,
            window_end:         s.window_end     ? moment(s.window_end)     : null,
            assigned_assessors: assigned,
        });
        setSingleOpen(true);
    };

    const handleSingleSave = async () => {
        if (!singleForm.facility_id || !singleForm.scheduled_date) {
            message.error('Facility and Scheduled Date are required');
            return;
        }
        if (!editSched) {
            const facility  = facilities.find(f => f.id === singleForm.facility_id);
            const clause    = clauses.find(c => c.id === singleForm.clause_id);
            const candidate = [{
                facility_id:   singleForm.facility_id,
                facility_name: facility?.business_name || singleForm.facility_id,
                clause_id:     singleForm.clause_id || '',
                clause_code:   clause?.code || '(full audit)',
                scheduled_date: moment(singleForm.scheduled_date).format('YYYY-MM-DD'),
            }];
            const dupes = findDuplicates(schedules, candidate);
            if (dupes.length > 0) {
                const d = dupes[0];
                Modal.error({
                    title: 'Duplicate schedule',
                    content: (
                        <div>
                            <strong>{d.facility_name}</strong> already has a{' '}
                            <strong>{d.clause_code}</strong> schedule in{' '}
                            <strong>{d.year}</strong>.
                            <div style={{ marginTop:8, fontSize:12, color:'#888' }}>
                                Only one active schedule per facility per clause per calendar year is allowed.
                            </div>
                        </div>
                    ),
                });
                return;
            }
        }
        setLoading(true);
        try {
            await QoCService.saveAuditSchedule({
                ...singleForm,
                scheduled_date: moment(singleForm.scheduled_date).format('YYYY-MM-DD'),
                window_start: singleForm.window_start ? moment(singleForm.window_start).format('YYYY-MM-DD') : null,
                window_end:   singleForm.window_end   ? moment(singleForm.window_end).format('YYYY-MM-DD')   : null,
                assigned_assessors: JSON.stringify(singleForm.assigned_assessors || []),
            });
            const facilityName = facilities.find(f => f.id === singleForm.facility_id)?.business_name || String(singleForm.facility_id);
            const clauseCode   = clauses.find(c => c.id === singleForm.clause_id)?.code || 'Full Audit';
            editSched
                ? trailUpdate(TRAIL_MODULES.AUDIT_SCHEDULES, `${facilityName} — ${clauseCode}`, editSched.id,
                    { status: singleForm.status, date: moment(singleForm.scheduled_date).format('YYYY-MM-DD') })
                : trailCreate(TRAIL_MODULES.AUDIT_SCHEDULES, `${facilityName} — ${clauseCode}`, '',
                    { date: moment(singleForm.scheduled_date).format('YYYY-MM-DD') });
            message.success('Schedule saved');
            setSingleOpen(false);
            load();
        } catch { message.error('Failed to save'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        try {
            const sched = schedules.find(s => s.id === id);
            await QoCService.deleteAuditSchedule(id);
            trailDelete(TRAIL_MODULES.AUDIT_SCHEDULES,
                `${sched?.facility_name || id} — ${sched?.clause_code || 'Full Audit'}`, id,
                { status: sched?.status, date: sched?.scheduled_date });
            message.success('Deleted');
            load();
        } catch { message.error('Failed to delete'); }
    };

    const updateBulkPreview = (form) => {
        if (!form.clause_ids?.length || !form.scheduled_date) {
            setBulkFacilities([]);
            setBulkPreview([]);
            return;
        }
        let facs = [];
        const mode = form.selection_mode || 'level';
        if (mode === 'level') {
            if (!form.facility_level) { setBulkFacilities([]); setBulkPreview([]); return; }
            facs = facilities.filter(f => f.site_type === form.facility_level);
        } else if (mode === 'region') {
            if (!form.region) { setBulkFacilities([]); setBulkPreview([]); return; }
            facs = facilities.filter(f => (f.located_region||f.region) === form.region);
        } else if (mode === 'custom') {
            const ids = form.custom_facility_ids || [];
            if (!ids.length) { setBulkFacilities([]); setBulkPreview([]); return; }
            facs = facilities.filter(f => ids.includes(f.id));
        }
        setBulkFacilities(facs);
        const rows = [];
        facs.forEach(fac => {
            form.clause_ids.forEach(cid => {
                const cl = clauses.find(c => c.id === cid);
                rows.push({
                    facility_id:        fac.id,
                    facility_name:      fac.business_name,
                    clause_id:          cid,
                    clause_code:        cl?.code,
                    clause_title:       cl?.title,
                    scheduled_date:     moment(form.scheduled_date).format('YYYY-MM-DD'),
                    window_start:       form.window_start ? moment(form.window_start).format('YYYY-MM-DD') : null,
                    window_end:         form.window_end   ? moment(form.window_end).format('YYYY-MM-DD')   : null,
                    lead_assessor_id:   form.lead_assessor_id || null,
                    assigned_assessors: form.assigned_assessors || [],
                    status:             'scheduled',
                    notes:              form.notes || '',
                });
            });
        });
        setBulkPreview(rows);
    };

    const setBulkField = (k, v) => {
        const updated = { ...bulkForm, [k]: v };
        setBulkForm(updated);
        updateBulkPreview(updated);
    };

    const handleBulkSave = async () => {
        if (!bulkPreview.length) { message.error('Nothing to schedule'); return; }
        const dupes = findDuplicates(schedules, bulkPreview);
        if (dupes.length > 0) {
            Modal.confirm({
                title: `${dupes.length} duplicate schedule${dupes.length > 1 ? 's' : ''} detected`,
                icon: null,
                width: 560,
                content: (
                    <div>
                        <div style={{ marginBottom:10, color:'#555', fontSize:13 }}>
                            The following already have an active schedule for the same calendar year.
                            Duplicates will be <strong>skipped</strong>. Continue?
                        </div>
                        <div style={{ maxHeight:200, overflowY:'auto',
                            border:'1px solid #ffe0e0', borderRadius:6,
                            background:'#fff8f8', padding:'8px 12px' }}>
                            {dupes.map((d, i) => (
                                <div key={i} style={{ fontSize:12, color:'#f5222d',
                                    padding:'3px 0', borderBottom: i < dupes.length-1 ? '1px solid #ffe0e0' : 'none' }}>
                                    <strong>{d.facility_name}</strong> — {d.clause_code} — {d.year}
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop:10, fontSize:12, color:'#888' }}>
                            {bulkPreview.length - dupes.length} new schedule(s) will be created.
                        </div>
                    </div>
                ),
                okText: `Create ${bulkPreview.length - dupes.length} new schedule(s)`,
                okButtonProps: {
                    disabled: bulkPreview.length - dupes.length === 0,
                    style: { background:'#7c3aed', borderColor:'#7c3aed' }
                },
                cancelText: 'Cancel',
                onOk: () => doBulkSave(dupes),
            });
            return;
        }
        doBulkSave([]);
    };

    const doBulkSave = async (dupesToSkip) => {
        const dupeKeys = new Set(
            dupesToSkip.map(d => `${d.facility_name}|${d.clause_code}|${d.year}`)
        );
        const toSave = bulkPreview.filter(row => {
            const year = moment(row.scheduled_date).year();
            const key  = `${row.facility_name}|${row.clause_code || '(full audit)'}|${year}`;
            return !dupeKeys.has(key);
        });
        if (!toSave.length) { message.info('No new schedules to create.'); return; }
        setBulkSaving(true);
        try {
            const res = await QoCService.bulkSaveSchedules(toSave);
            const count = res.data?.data?.count || toSave.length;
            trailCreate(TRAIL_MODULES.AUDIT_SCHEDULES, 'Bulk Schedule', '',
                { count, facilityCount: bulkFacilities.length,
                  clauseCount: bulkForm.clause_ids?.length,
                  mode: bulkForm.selection_mode,
                  date: moment(bulkForm.scheduled_date).format('YYYY-MM-DD') });
            message.success(`Created ${count} schedules`);
            setBulkOpen(false);
            setBulkForm({ selection_mode:'level', facility_level:null, region:null,
                custom_facility_ids:[], clause_ids:[], scheduled_date:null,
                window_start:null, window_end:null, lead_assessor_id:null,
                assigned_assessors:[], notes:'' });
            setBulkPreview([]);
            load();
        } catch (e) { message.error(e.response?.data?.message || 'Failed to save'); }
        finally { setBulkSaving(false); }
    };

    const today = moment().startOf('day');

    const columns = [
        {
            title: 'Facility', dataIndex: 'facility_name', ellipsis: true, width: 180,
            render: (name, r) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{name}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>{r.site_type} • {r.region}</div>
                </div>
            )
        },
        {
            title: 'Clause', dataIndex: 'clause_code', width: 130,
            render: (code, r) => {
                if (!code) return <Tag>Full Audit</Tag>;
                const cl  = clauses.find(c => c.code === code);
                const col = clauseColor(cl, standards);
                return (
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <div style={{ width:28, height:22, borderRadius:4, background:col,
                            color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:9, fontWeight:800, padding:'0 4px', whiteSpace:'nowrap' }}>{code}</div>
                        <span style={{ fontSize:10, color:'#555' }}>{(r.clause_title||'').slice(0,16)}</span>
                    </div>
                );
            }
        },
        {
            title: 'Scheduled Date', dataIndex: 'scheduled_date', width: 120,
            render: (d, r) => {
                const overdue = r.status !== 'completed' && r.status !== 'cancelled'
                    && moment(d).isBefore(today);
                return (
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 12, color: overdue ? '#f5222d' : '#333' }}>
                            {moment(d).format('DD MMM YYYY')}
                        </div>
                        {overdue && <Tag color="red" style={{ fontSize: 9 }}>
                            {today.diff(moment(d),'days')}d overdue
                        </Tag>}
                    </div>
                );
            }
        },
        {
            title: 'Window', width: 140,
            render: (_, r) => r.window_start ? (
                <span style={{ fontSize: 11, color: '#888' }}>
                    {moment(r.window_start).format('DD MMM')} → {r.window_end ? moment(r.window_end).format('DD MMM YY') : '—'}
                </span>
            ) : <span style={{ color: '#ccc' }}>—</span>
        },
        {
            title: 'Lead Assessor', dataIndex: 'lead_assessor_name', width: 130,
            render: v => v || <span style={{ color: '#ccc' }}>Unassigned</span>
        },
        {
            title: 'Status', dataIndex: 'status', width: 115,
            render: s => {
                const cfg = STATUS_CFG[s] || STATUS_CFG.scheduled;
                return <Tag color={cfg.color} style={{ fontWeight: 700, fontSize: 11 }}>{cfg.label}</Tag>;
            }
        },
        {
            title: 'Assessment', width: 100,
            render: (_, r) => {
                if (!r.assessment_id) return <span style={{ color:'#ccc', fontSize:11 }}>—</span>;
                return (
                    <div>
                        <span style={{ fontWeight:700, fontSize:12,
                            color: r.assessment_status==='approved'?'#52c41a':'#faad14' }}>
                            {parseFloat(r.assessment_score||0).toFixed(1)}%
                        </span>
                        <div style={{ fontSize:10, color:'#888' }}>{r.assessment_status}</div>
                    </div>
                );
            }
        },
        {
            title: '', width: 80,
            render: (_, r) => (
                <div style={{ display:'flex', gap:4 }}>
                    {isAdmin && <Button size="small" onClick={() => openEdit(r)}>Edit</Button>}
                    {isAdmin && <Button size="small" danger onClick={() => handleDelete(r.id)}>✕</Button>}
                </div>
            )
        },
    ];

    const overdueCount = schedules.filter(s =>
        s.status !== 'completed' && s.status !== 'cancelled'
        && moment(s.scheduled_date).isBefore(today)
    ).length;

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Audit Schedules</h2></div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8 }}>
                    {isAdmin && <Button icon={<ThunderboltOutlined/>} type="primary"
                        style={{ background:'#7c3aed', borderColor:'#7c3aed' }}
                        onClick={() => setBulkOpen(true)}>
                        Bulk Schedule by Level
                    </Button>}
                    {isAdmin && <Button icon={<PlusOutlined/>} type="primary" onClick={openNew}>
                        New Schedule
                    </Button>}
                </div>
            </div>

            <div className="page-pad">
                <Row gutter={12} style={{ marginBottom: 16 }}>
                    {[
                        { label:'Total',       val: schedules.length,                                    color:'#1890ff' },
                        { label:'Scheduled',   val: schedules.filter(s=>s.status==='scheduled').length,  color:'#06b6d4' },
                        { label:'In Progress', val: schedules.filter(s=>s.status==='in_progress').length,color:'#faad14' },
                        { label:'Completed',   val: schedules.filter(s=>s.status==='completed').length,  color:'#52c41a' },
                        { label:'Overdue',     val: overdueCount,                                        color:'#f5222d' },
                    ].map(s => (
                        <Col key={s.label} span={4}>
                            <div style={{ background:`${s.color}09`, border:`1px solid ${s.color}28`,
                                borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                                <div style={{ fontSize:10, color:'#888', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8 }}>{s.label}</div>
                                <div style={{ fontSize:28, fontWeight:900, color:s.color }}>{s.val}</div>
                            </div>
                        </Col>
                    ))}
                    <Col span={4}>
                        {schedules.length > 0 && (
                            <div style={{ background:'#f0f5ff', border:'1px solid #dbeafe',
                                borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                                <div style={{ fontSize:10, color:'#888', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8 }}>% Complete</div>
                                <Progress type="circle"
                                    percent={Math.round(schedules.filter(s=>s.status==='completed').length/schedules.length*100)}
                                    width={50} strokeColor="#52c41a"
                                    format={v=><span style={{ fontSize:12, fontWeight:800 }}>{v}%</span>}/>
                            </div>
                        )}
                    </Col>
                </Row>

                <Card size="small" style={{ marginBottom: 14 }}>
                    <Row gutter={12} align="middle">
                        <Col span={7}>
                            <Select allowClear placeholder="Filter by Facility" style={{ width:'100%' }}
                                showSearch optionFilterProp="children"
                                value={filters.facility_id || undefined}
                                onChange={v => setFilters(f=>({...f, facility_id:v||''}))}>
                                {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select allowClear placeholder="Status" style={{ width:'100%' }}
                                value={filters.status || undefined}
                                onChange={v => setFilters(f=>({...f, status:v||''}))}>
                                {Object.entries(STATUS_CFG).map(([k,v]) => <Option key={k} value={k}>{v.label}</Option>)}
                            </Select>
                        </Col>
                        <Col span={3}><Button type="primary" onClick={load} loading={loading}>Search</Button></Col>
                        <Col span={3}>
                            <Button onClick={() => { setFilters({ status:'', facility_id:'' }); setTimeout(load,50); }}>Clear</Button>
                        </Col>
                    </Row>
                </Card>

                <Card size="small">
                    <Table columns={columns} dataSource={schedules} rowKey="id" loading={loading}
                        pagination={{ pageSize:20, showTotal:t=>`${t} schedules` }}
                        rowClassName={r => {
                            if (r.status==='completed') return '';
                            if (moment(r.scheduled_date).isBefore(today)) return 'ant-table-row-overdue';
                            return '';
                        }}/>
                </Card>
            </div>

            {/* ── Single schedule modal ── */}
            <Modal title={editSched ? 'Edit Schedule' : 'New Audit Schedule'}
                open={singleOpen} onCancel={() => setSingleOpen(false)}
                onOk={handleSingleSave} confirmLoading={loading} okText="Save" width={700}>
                <Row gutter={16}>
                    <Col span={12}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Facility *</label>
                        <AntdSelect showSearch options={facilities} value={singleForm.facility_id}
                            onChange={v => setSingleForm({...singleForm, facility_id:v})} style={{ width:'100%' }}/>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>
                            Clause <span style={{ fontWeight:400, color:'#aaa', fontSize:11 }}>(blank = full audit)</span>
                        </label>
                        {/* GROUPED by standard */}
                        <Select allowClear placeholder="Full Audit" value={singleForm.clause_id}
                            onChange={v => setSingleForm({...singleForm, clause_id:v})}
                            style={{ width:'100%' }} showSearch optionFilterProp="children">
                            {renderClauseOptGroups()}
                        </Select>
                    </Col>
                    <Col span={8} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Scheduled Date *</label>
                        <AntdDatepicker value={singleForm.scheduled_date}
                            onChange={v => {
                                if (!v) { setSingleForm({...singleForm, scheduled_date:null}); return; }
                                const updates = { scheduled_date: v };
                                if (!singleForm.window_start) updates.window_start = v;
                                if (!singleForm.window_end) {
                                    const cur = currentSchedPeriod;
                                    updates.window_end = cur ? moment(cur.period_end) : moment(v).add(30,'days');
                                }
                                setSingleForm({...singleForm, ...updates});
                            }} style={{ width:'100%' }}/>
                    </Col>
                    <Col span={8} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Window Start</label>
                        <AntdDatepicker value={singleForm.window_start}
                            onChange={v => setSingleForm({...singleForm, window_start:v})} style={{ width:'100%' }}/>
                    </Col>
                    <Col span={8} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Window End</label>
                        <AntdDatepicker value={singleForm.window_end}
                            onChange={v => setSingleForm({...singleForm, window_end:v})} style={{ width:'100%' }}/>
                    </Col>
                    <Col span={12} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Lead Assessor</label>
                        <AntdSelect showSearch options={users} value={singleForm.lead_assessor_id}
                            onChange={v => setSingleForm({...singleForm, lead_assessor_id:v})} style={{ width:'100%' }}/>
                    </Col>
                    <Col span={12} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Status</label>
                        <Select value={singleForm.status}
                            onChange={v => setSingleForm({...singleForm, status:v})} style={{ width:'100%' }}>
                            {Object.entries(STATUS_CFG).map(([k,v]) => <Option key={k} value={k}>{v.label}</Option>)}
                        </Select>
                    </Col>
                    <Col span={24} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>
                            Additional Assessors
                            <span style={{ fontWeight:400, fontSize:11, color:'#888', marginLeft:8 }}>
                                These assessors will also see this schedule on their Assessments page
                            </span>
                        </label>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Select additional assessors (optional)"
                            style={{ width:'100%' }}
                            value={singleForm.assigned_assessors || []}
                            onChange={v => setSingleForm({...singleForm, assigned_assessors: v})}
                            optionFilterProp="label"
                            options={users
                                .filter(u => u.id !== singleForm.lead_assessor_id)
                                .map(u => ({ value: u.id, label: u.label || u.name }))}
                        />
                    </Col>
                    <Col span={24} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Notes</label>
                        <TextArea rows={2} value={singleForm.notes}
                            onChange={e => setSingleForm({...singleForm, notes:e.target.value})}
                            placeholder="Optional notes..."/>
                    </Col>
                </Row>
            </Modal>

            {/* ── BULK SCHEDULE MODAL ── */}
            <Modal
                title={<span><ThunderboltOutlined style={{ color:'#7c3aed', marginRight:8 }}/>
                    Bulk Schedule Audit</span>}
                open={bulkOpen}
                onCancel={() => { setBulkOpen(false); setBulkPreview([]); }}
                onOk={handleBulkSave}
                confirmLoading={bulkSaving}
                okText={`Create ${bulkPreview.length} Schedules`}
                okButtonProps={{ disabled: bulkPreview.length === 0,
                    style:{ background:'#7c3aed', borderColor:'#7c3aed' } }}
                width={960}
                style={{ top:20 }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:6 }}>Schedule By *</label>
                                <Select style={{ width:'100%' }}
                                    value={bulkForm.selection_mode || 'level'}
                                    onChange={v => setBulkField('selection_mode', v)}>
                                    <Select.Option value="level">🏗 By Facility Level</Select.Option>
                                    <Select.Option value="region">🗺 By Region</Select.Option>
                                    <Select.Option value="custom">☑ Custom Selection</Select.Option>
                                </Select>
                            </div>

                            {(!bulkForm.selection_mode || bulkForm.selection_mode === 'level') && (
                                <div>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Facility Level *</label>
                                    <Select placeholder="Select Level" style={{ width:'100%' }}
                                        value={bulkForm.facility_level}
                                        onChange={v => setBulkField('facility_level', v)}>
                                        {FACILITY_LEVELS.map(l => (
                                            <Select.Option key={l} value={l}>
                                                {l}
                                                <span style={{ color:'#aaa', marginLeft:8, fontSize:11 }}>
                                                    ({facilities.filter(f=>f.site_type===l).length} facilities)
                                                </span>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    {bulkFacilities.length > 0 && (
                                        <div style={{ marginTop:6, fontSize:11, color:'#1890ff' }}>
                                            {bulkFacilities.length} facilities matched
                                        </div>
                                    )}
                                </div>
                            )}

                            {bulkForm.selection_mode === 'region' && (
                                <div>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Region *</label>
                                    <Select placeholder="Select Region" style={{ width:'100%' }}
                                        value={bulkForm.region}
                                        onChange={v => setBulkField('region', v)}>
                                        {[...new Set(facilities.map(f=>f.located_region||f.region).filter(Boolean))].sort().map(r => (
                                            <Select.Option key={r} value={r}>
                                                {r}
                                                <span style={{ color:'#aaa', marginLeft:8, fontSize:11 }}>
                                                    ({facilities.filter(f=>(f.located_region||f.region)===r).length} facilities)
                                                </span>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            )}

                            {bulkForm.selection_mode === 'custom' && (
                                <div>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                        Select Facilities *
                                        <span style={{ fontWeight:400, fontSize:11, color:'#888', marginLeft:8 }}>
                                            ({(bulkForm.custom_facility_ids||[]).length} selected)
                                        </span>
                                    </label>
                                    <div style={{ display:'flex', gap:6, marginBottom:6 }}>
                                        <Button size="small" onClick={() => setBulkField('custom_facility_ids', facilities.map(f=>f.id))}>Select All</Button>
                                        <Button size="small" onClick={() => setBulkField('custom_facility_ids', [])}>Clear</Button>
                                        <Select allowClear size="small" placeholder="Filter by level" style={{ width:140 }}
                                            onChange={v => {
                                                if (!v) return;
                                                const ids = facilities.filter(f=>f.site_type===v).map(f=>f.id);
                                                setBulkField('custom_facility_ids', ids);
                                            }}>
                                            {FACILITY_LEVELS.map(l => <Select.Option key={l} value={l}>{l}</Select.Option>)}
                                        </Select>
                                    </div>
                                    <div style={{ maxHeight:180, overflowY:'auto',
                                        border:'1px solid #e8e8e8', borderRadius:6, padding:'4px 0' }}>
                                        <Checkbox.Group
                                            style={{ display:'flex', flexDirection:'column' }}
                                            value={bulkForm.custom_facility_ids||[]}
                                            onChange={v => setBulkField('custom_facility_ids', v)}>
                                            {facilities.map(f => (
                                                <Checkbox key={f.id} value={f.id}
                                                    style={{ padding:'4px 10px', fontSize:11,
                                                        borderBottom:'1px solid #f5f5f5', margin:0 }}>
                                                    <span style={{ fontWeight:600 }}>{f.business_name}</span>
                                                    <span style={{ color:'#aaa', fontSize:10, marginLeft:6 }}>
                                                        {f.site_type} • {f.located_region||f.region}
                                                    </span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                </div>
                            )}

                            {/* Clauses grouped by standard */}
                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                    Clauses to Schedule *
                                </label>
                                <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                                    <Button size="small" onClick={() => setBulkField('clause_ids', clauses.map(c=>c.id))}>All Clauses</Button>
                                    <Button size="small" onClick={() => setBulkField('clause_ids', [])}>Clear</Button>
                                </div>
                                {clausesByStandard.map(({ standard, clauses: cls }) => (
                                    <div key={standard.id} style={{ marginBottom:10 }}>
                                        <div style={{ fontSize:11, fontWeight:700, color: standard.accent || '#555',
                                            marginBottom:4, textTransform:'uppercase', letterSpacing:0.5 }}>
                                            {standard.name}
                                        </div>
                                        <Checkbox.Group
                                            style={{ display:'flex', flexDirection:'column', gap:4 }}
                                            value={bulkForm.clause_ids}
                                            onChange={v => {
                                                // Merge this standard's selection with others
                                                const otherStdIds = (bulkForm.clause_ids || [])
                                                    .filter(id => !cls.find(c => c.id === id));
                                                setBulkField('clause_ids', [...otherStdIds, ...v]);
                                            }}>
                                            {cls.map(c => (
                                                <Checkbox key={c.id} value={c.id}>
                                                    <span style={{ fontWeight:700, color: standard.accent || '#1890ff', marginRight:4 }}>{c.code}</span>
                                                    <span style={{ fontSize:11, color:'#555' }}>{c.title?.slice(0,34)}</span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                ))}
                                {orphanClauses.length > 0 && (
                                    <div>
                                        <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:4 }}>Other</div>
                                        <Checkbox.Group
                                            style={{ display:'flex', flexDirection:'column', gap:4 }}
                                            value={bulkForm.clause_ids}
                                            onChange={v => setBulkField('clause_ids', v)}>
                                            {orphanClauses.map(c => (
                                                <Checkbox key={c.id} value={c.id}>
                                                    <span style={{ fontSize:11 }}>{c.code} — {c.title?.slice(0,34)}</span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                )}
                            </div>

                            <Row gutter={8}>
                                <Col span={24}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Scheduled Date *</label>
                                    <AntdDatepicker value={bulkForm.scheduled_date} style={{ width:'100%' }}
                                        onChange={v => setBulkField('scheduled_date', v)}/>
                                </Col>
                                <Col span={12} style={{ marginTop:8 }}>
                                    <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Window Start</label>
                                    <AntdDatepicker value={bulkForm.window_start} style={{ width:'100%' }}
                                        onChange={v => setBulkField('window_start', v)}/>
                                </Col>
                                <Col span={12} style={{ marginTop:8 }}>
                                    <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Window End</label>
                                    <AntdDatepicker value={bulkForm.window_end} style={{ width:'100%' }}
                                        onChange={v => setBulkField('window_end', v)}/>
                                </Col>
                            </Row>

                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Lead Assessor</label>
                                <AntdSelect showSearch options={users} value={bulkForm.lead_assessor_id}
                                    onChange={v => setBulkField('lead_assessor_id', v)} style={{ width:'100%' }}/>
                            </div>

                            <div>
                                <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Notes (applied to all)</label>
                                <TextArea rows={2} value={bulkForm.notes}
                                    onChange={e => setBulkField('notes', e.target.value)}
                                    placeholder="Optional notes..."/>
                            </div>
                        </div>
                    </Col>

                    <Col span={12}>
                        <div style={{ background:'#f8faff', borderRadius:8, padding:14,
                            border:'1px solid #e0eaff', height:'100%' }}>
                            <div style={{ fontWeight:800, fontSize:13, marginBottom:10,
                                display:'flex', alignItems:'center', gap:8 }}>
                                <CopyOutlined style={{ color:'#7c3aed' }}/>
                                Preview — {bulkPreview.length} schedules to create
                            </div>
                            {bulkPreview.length === 0
                                ? <div style={{ color:'#aaa', fontSize:12, textAlign:'center', padding:30 }}>
                                    Complete the form to preview
                                  </div>
                                : (
                                    <>
                                        <div style={{ marginBottom:10, fontSize:12 }}>
                                            <strong>{bulkFacilities.length}</strong> facilities ×{' '}
                                            <strong>{bulkForm.clause_ids?.length}</strong> clauses ={' '}
                                            <strong style={{ color:'#7c3aed' }}>{bulkPreview.length}</strong> schedules
                                        </div>
                                        <div style={{ maxHeight:380, overflowY:'auto' }}>
                                            {bulkFacilities.map(fac => (
                                                <div key={fac.id} style={{ marginBottom:8,
                                                    background:'#fff', borderRadius:6, padding:'7px 10px',
                                                    border:'1px solid #e8eeff' }}>
                                                    <div style={{ fontWeight:700, fontSize:12, color:'#333', marginBottom:4 }}>
                                                        {fac.business_name}
                                                        <span style={{ fontWeight:400, color:'#aaa', fontSize:10, marginLeft:6 }}>
                                                            {fac.site_type} · {fac.located_region||fac.region}
                                                        </span>
                                                    </div>
                                                    <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                                                        {(bulkForm.clause_ids||[]).map(cid => {
                                                            const cl  = clauses.find(c=>c.id===cid);
                                                            const col = clauseColor(cl, standards);
                                                            return (
                                                                <Tag key={cid}
                                                                    style={{ background:col, color:'#fff',
                                                                        border:'none', fontSize:9, fontWeight:700 }}>
                                                                    {cl?.code}
                                                                </Tag>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <Divider style={{ margin:'10px 0' }}/>
                                        <div style={{ fontSize:11, color:'#888' }}>
                                            Date: <strong>{bulkForm.scheduled_date ? moment(bulkForm.scheduled_date).format('DD MMM YYYY') : '—'}</strong>
                                            {bulkForm.window_end && <> | Window ends: <strong>{moment(bulkForm.window_end).format('DD MMM YYYY')}</strong></>}
                                        </div>
                                    </>
                                )}
                        </div>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
}